/**
 * Client-side report generation — no backend job queue needed.
 * Produces Excel, CSV, PDF (print dialog), or Web (new tab) from
 * already-fetched attendance / employee data.
 */
import * as XLSX from 'xlsx';

// ─── Row builders per report type ─────────────────────────────────────────────

export function buildDetailRows(records) {
  return records.map(r => ({
    'Employee Code':  r.employee_code || '',
    'Name':           r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim(),
    'Department':     r.department?.name || r.department || '',
    'Designation':    r.designation || '',
    'Date':           r.date || '',
    'Status':         r.status || '',
    'Check In':       r.check_in || '',
    'Check Out':      r.check_out || '',
    'Hours Worked':   r.hours_worked || r.work_hours || '',
    'Leave Type':     r.leave_type || '',
    'Method':         r.method || '',
    'Late By (min)':  r.late_by_minutes || r.late_by || '',
  }));
}

function buildSummaryRows(records) {
  const deptMap = {};
  records.forEach(r => {
    const dept = r.department?.name || r.department || 'Unknown';
    if (!deptMap[dept]) deptMap[dept] = { Department: dept, Present: 0, Absent: 0, Late: 0, 'Half Day': 0, Leave: 0, Holiday: 0, 'Weekly Off': 0, Total: 0 };
    const s = r.status || 'ABSENT';
    const label = s === 'HALF_DAY' ? 'Half Day' : s === 'WEEKLY_OFF' ? 'Weekly Off' : s.charAt(0) + s.slice(1).toLowerCase();
    if (deptMap[dept][label] !== undefined) deptMap[dept][label]++;
    deptMap[dept].Total++;
  });
  return Object.values(deptMap);
}

function buildLateRows(records) {
  return records
    .filter(r => r.status === 'LATE')
    .map(r => ({
      'Employee Code': r.employee_code || '',
      'Name':         r.name || '',
      'Department':   r.department?.name || r.department || '',
      'Date':         r.date || '',
      'Check In':     r.check_in || '',
      'Late By (min)':r.late_by_minutes || r.late_by || '',
      'Method':       r.method || '',
    }));
}

function getRows(type, records) {
  switch (type) {
    case 'attendance_summary': return buildSummaryRows(records);
    case 'late_arrivals':      return buildLateRows(records);
    default:                   return buildDetailRows(records);
  }
}

function getTitle(type) {
  return {
    attendance_summary: 'Attendance Summary',
    attendance_detail:  'Detailed Attendance Register',
    late_arrivals:      'Late Arrivals Report',
  }[type] || 'Attendance Report';
}

// ─── HTML template for PDF / Web ─────────────────────────────────────────────

function buildHtml(rows, title, filterSummary, companyName) {
  const headers = rows.length ? Object.keys(rows[0]) : [];
  const trRows = rows.map(r =>
    `<tr>${headers.map(h => `<td>${r[h] ?? ''}</td>`).join('')}</tr>`
  ).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${title} — ${companyName}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; color: #111; }
    h2 { font-size: 15px; margin: 0 0 4px; }
    .meta { color: #666; font-size: 10px; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f0f0f0; border: 1px solid #ccc; padding: 5px 8px; text-align: left; font-size: 10px; text-transform: uppercase; }
    td { border: 1px solid #ddd; padding: 4px 8px; }
    tr:nth-child(even) { background: #fafafa; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <h2>${title} — ${companyName}</h2>
  <div class="meta">${filterSummary || 'All employees'} &nbsp;·&nbsp; ${rows.length} records &nbsp;·&nbsp; Generated ${new Date().toLocaleString('en-IN')}</div>
  <table>
    <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>${trRows || '<tr><td colspan="99" style="text-align:center;color:#999">No records</td></tr>'}</tbody>
  </table>
</body>
</html>`;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * @param {string} type - report type id from REPORT_TYPES
 * @param {string} dateFrom
 * @param {string} dateTo
 * @param {Array}  records - attendance records already fetched
 * @param {string} format - 'xlsx' | 'csv' | 'pdf' | 'web'
 * @param {string} companyName
 * @param {string} filterSummary - human-readable filter description
 */
export function generateReport({ type, dateFrom, dateTo, records = [], format, companyName = 'AttendX', filterSummary = '' }) {
  const rows = getRows(type, records);
  const title = getTitle(type);
  const filename = `attendx-${type}-${dateFrom}`;

  switch (format) {
    case 'xlsx': {
      const wb = XLSX.utils.book_new();
      const ws = rows.length
        ? XLSX.utils.json_to_sheet(rows)
        : XLSX.utils.aoa_to_sheet([['No records found for the selected period']]);
      XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
      XLSX.writeFile(wb, `${filename}.xlsx`);
      break;
    }

    case 'csv': {
      const wb = XLSX.utils.book_new();
      const ws = rows.length
        ? XLSX.utils.json_to_sheet(rows)
        : XLSX.utils.aoa_to_sheet([['No records found for the selected period']]);
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, `${filename}.csv`, { bookType: 'csv' });
      break;
    }

    case 'pdf': {
      const html = buildHtml(rows, title, filterSummary, companyName);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      iframe.srcdoc = html;
      iframe.onload = () => {
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 3000);
      };
      break;
    }

    case 'web': {
      const html = buildHtml(rows, title, filterSummary, companyName);
      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      break;
    }

    default:
      console.warn('Unknown report format:', format);
  }
}

// ─── Specialised row builders (used by Report pages) ──────────────────────────

/**
 * Active Employee Report rows.
 * @param {Array} employees - from useEmployees / employee-service
 */
export function buildActiveEmployeeRows(employees = []) {
  return employees
    .filter(e => e.status === 'ACTIVE')
    .map(e => ({
      'Employee Code':    e.employee_code || '',
      'Name':             `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.name || '',
      'Department':       e.department?.name || e.department || '',
      'Designation':      e.designation || '',
      'Employment Type':  e.employment_type || '',
      'Joining Date':     e.joining_date || '',
      'Email':            e.email || '',
      'Phone':            e.phone || '',
      'Status':           e.status || '',
    }));
}

/**
 * Monthly Attendance Report rows — flattens the day-grid into one row per employee.
 * @param {Array} employees - from useMonthlyAllAttendance
 */
export function buildMonthlyGridRows(employees = []) {
  return employees.map(e => {
    const row = {
      'Employee Code': e.employee_code || '',
      'Name':          e.name || '',
      'Department':    e.department || '',
      'Designation':   e.designation || '',
    };

    // Day-by-day status columns (1–31)
    const dayKeys = Object.keys(e.days || {}).sort((a, b) => Number(a) - Number(b));
    for (const d of dayKeys) {
      row[`Day ${d}`] = e.days[d] || '';
    }

    // Summary columns
    const s = e.summary || {};
    row['Present (P)']  = s.P  ?? 0;
    row['Absent (A)']   = s.A  ?? 0;
    row['Half Day (L)'] = s.L  ?? 0;
    row['Holiday (H)']  = s.H  ?? 0;
    row['Weekly Off (S)'] = s.S ?? 0;
    row['OT']           = s.OT || '0h 0m';

    return row;
  });
}

/**
 * Swipe (Punch Log) Report rows.
 * @param {Array} punchLogs - from usePunchLogs
 */
export function buildSwipeRows(punchLogs = []) {
  return punchLogs.map(p => ({
    'Date':          p.date || '',
    'Time':          p.punch_time ? p.punch_time.slice(11, 16) : '',
    'Employee':      p.employee_name || '',
    'Employee Code': p.employee_code || '',
    'Department':    p.department || '',
    'Device':        p.device_name || p.device_id || '',
    'Punch Type':    p.punch_type || '',
    'Source':        p.source || '',
    'Status':        p.status || '',
  }));
}

/**
 * Strength Report rows.
 * @param {Array} deptData - [{ department, total, present, absent, leave, late }]
 */
export function buildStrengthRows(deptData = []) {
  return deptData.map(d => ({
    'Department':   d.department || '',
    'Total':        d.total ?? 0,
    'Present':      d.present ?? 0,
    'Absent':       d.absent ?? 0,
    'On Leave':     d.leave ?? 0,
    'Late':         d.late ?? 0,
    'Attendance %': d.total
      ? `${Math.round(((d.present || 0) / d.total) * 100)}%`
      : '0%',
  }));
}

/**
 * OT Report rows.
 * @param {Array} employees - from useMonthlyAllAttendance (with OT data in summary)
 */
export function buildOTRows(employees = []) {
  return employees
    .filter(e => {
      // Include only employees who actually have OT
      const ot = e.summary?.OT || '0h 0m';
      return ot !== '0h 0m' && ot !== '0h 0min';
    })
    .map(e => {
      const times = e.times || {};
      let otDays = 0;

      for (const t of Object.values(times)) {
        if (t && t.ot_min > 0) otDays++;
      }

      return {
        'Employee Code': e.employee_code || '',
        'Employee':      e.name || '',
        'Department':    e.department || '',
        'Designation':   e.designation || '',
        'OT Days':       otDays,
        'Total OT':      e.summary?.OT || '0h 0m',
        'Present Days':  e.summary?.P ?? 0,
      };
    });
}

// ─── Generic lower-level export ────────────────────────────────────────────────

/**
 * Like generateReport() but accepts pre-built rows and a title directly.
 * Used by ReportExportBar so report pages don't need to call getRows/getTitle.
 *
 * @param {object} opts
 * @param {Array}   opts.rows         - pre-built row objects (keys become column headers)
 * @param {string}  opts.title        - report title shown in PDF/web view
 * @param {string}  opts.filename     - base filename without extension
 * @param {string}  opts.format       - 'xlsx' | 'csv' | 'pdf' | 'web'
 * @param {string}  opts.companyName
 * @param {string}  opts.filterSummary - human-readable filter description
 */
export function generateCustomReport({ rows = [], title = 'Report', filename = 'report', format, companyName = 'AttendX', filterSummary = '' }) {
  switch (format) {
    case 'xlsx': {
      const wb = XLSX.utils.book_new();
      const ws = rows.length
        ? XLSX.utils.json_to_sheet(rows)
        : XLSX.utils.aoa_to_sheet([['No records found for the selected period']]);
      XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
      XLSX.writeFile(wb, `${filename}.xlsx`);
      break;
    }

    case 'csv': {
      const wb = XLSX.utils.book_new();
      const ws = rows.length
        ? XLSX.utils.json_to_sheet(rows)
        : XLSX.utils.aoa_to_sheet([['No records found for the selected period']]);
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, `${filename}.csv`, { bookType: 'csv' });
      break;
    }

    case 'pdf': {
      const html = buildHtml(rows, title, filterSummary, companyName);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      iframe.srcdoc = html;
      iframe.onload = () => {
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 3000);
      };
      break;
    }

    case 'web': {
      const html = buildHtml(rows, title, filterSummary, companyName);
      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      break;
    }

    default:
      console.warn('Unknown report format:', format);
  }
}
